import { ActionModal, Button, Text, Input, useThemeColor, useToast, Avatar } from '@wistia/vhs';
import { isEmptyString, isNil, isNotNil } from '~/utilities/type-guards';
import type { Contact } from '~/graphql';
import { styled } from 'styled-components';
import type { AsyncNoOpFn, NoOpFn } from '~/utilities/util';
import type { JSX } from 'react';
import { useRef, useState } from 'react';
import { generateAnonInitials } from '~/singlePageApp/screens/Media/Comments/utils';
import { useCreateContactRequestMutation } from './__gql/RequestToBeAUserModal.gql.gen';

const LoggedInUserContainer = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.color.grey100};
  border: ${({ theme }) => `${theme.color.grey200} solid 1px`};
  border-radius: ${({ theme }) => theme.spacing.space01};
  display: flex;
  gap: ${({ theme }) => theme.spacing.space03};
  justify-content: start;
  padding: ${({ theme }) => theme.spacing.space02};
`;

const ErrorMessage = styled.div`
  background: ${({ theme }) => theme.color.red100};
  border: ${({ theme }) => `${theme.color.red200} solid 1px`};
  border-radius: ${({ theme }) => theme.spacing.space01};
  padding: ${({ theme }) => theme.spacing.space04};
`;

const StackedInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ModalContent = styled(StackedInfo)`
  gap: ${({ theme }) => theme.spacing.space01};
`;

type ModalToggle = (show: AsyncNoOpFn | NoOpFn) => JSX.Element;
type RequestState = { sent: boolean; failed: boolean };
export type ContactData = Pick<Contact, 'email' | 'id' | 'initials' | 'name'> & {
  avatar?: {
    __typename?: 'Avatar';
    id: string;
    thumbnailUrl?: string | null | undefined;
  } | null;
};

export const LoggedInUser = ({
  name,
  email,
  contact,
}: {
  name?: string;
  email?: string;
  contact: ContactData | null | undefined;
}): JSX.Element => {
  const grey700 = useThemeColor('grey700');
  const { name: contactName, email: contactEmail, initials, avatar } = contact ?? {};

  const displayName = contactName ?? name ?? '';
  const displayEmail = contactEmail ?? email ?? '';

  return (
    <LoggedInUserContainer>
      <Avatar
        name={displayName}
        initials={initials ?? generateAnonInitials(displayName)}
        imageUrl={avatar?.thumbnailUrl}
        heightAndWidth={32}
      />
      <StackedInfo>
        <Text variant="subtitle2">{displayName}</Text>
        <Text
          variant="caption"
          colorOverride={grey700}
        >
          {displayEmail}
        </Text>
      </StackedInfo>
    </LoggedInUserContainer>
  );
};

const LoggedInModal = ({
  contact,
  toggle,
  mediaId,
}: {
  contact: ContactData;
  toggle: ModalToggle;
  mediaId: string;
}): JSX.Element => {
  const [requestSent, setRequestSent] = useState<boolean>(false);
  const showToast = useToast();

  const [submitContactRequest] = useCreateContactRequestMutation({
    variables: {
      input: { email: contact.email, mediaId },
    },
    onError: () => {
      showToast({
        message: 'There was an error processing your request.',
        variant: 'error',
      });
    },
  });

  return (
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    <ActionModal
      title="Request Sent"
      toggle={(show: NoOpFn) => {
        const onLaunchModal = async () => {
          if (requestSent) {
            show();
          } else {
            const { data } = await submitContactRequest();
            if (isNotNil(data)) {
              setRequestSent(true);
              show();
            }
          }
        };
        return toggle(onLaunchModal);
      }}
      buttons={(hide: NoOpFn) => (
        <Button
          label="Close"
          onClick={hide}
        />
      )}
    >
      <ModalContent>
        <Text variant="body2">
          Your request has been sent. When your request is accepted, we&apos;ll send you an email to
          let you know.
        </Text>
        <Text
          variant="caption"
          bold={true}
        >
          You&apos;re signed in as:
        </Text>
        <LoggedInUser contact={contact} />
      </ModalContent>
    </ActionModal>
  );
};

const SentRequestMessage = ({ failed, email }: { failed: boolean; email: string }): JSX.Element => {
  const red700 = useThemeColor('red700');

  return failed ? (
    <ErrorMessage>
      <Text colorOverride={red700}>
        There was an error processing your request. Please reload the page and try again, or contact
        the account admin.
      </Text>
    </ErrorMessage>
  ) : (
    <Text>
      We&apos;ve sent an email to <strong>{email}</strong>. Once you receive the email, click the
      included link to confirm the request.
    </Text>
  );
};

const AnonymousModal = ({
  toggle,
  mediaId,
}: {
  toggle: ModalToggle;
  mediaId: string;
}): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [contactEmail, setContactEmail] = useState<string>('');
  const [requestState, setRequestState] = useState<RequestState>({ sent: false, failed: false });
  const [hasInputError, setHasInputError] = useState<boolean>(false);

  const [submitContactRequest] = useCreateContactRequestMutation({
    onError: () => setRequestState({ sent: true, failed: true }),
    onCompleted: () => setRequestState({ sent: true, failed: false }),
  });

  const onSubmitRequest = () => {
    const enteredEmail = inputRef.current?.value;

    if (isNil(enteredEmail) || isEmptyString(enteredEmail.trim())) {
      setHasInputError(true);
    } else {
      setContactEmail(enteredEmail);
      setHasInputError(false);

      void submitContactRequest({
        variables: {
          input: { email: enteredEmail, mediaId },
        },
      });
    }
  };

  return (
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    <ActionModal
      title="Request Access"
      toggle={toggle}
      buttons={(hide: NoOpFn) => (
        <Button
          label={requestState.sent ? 'Close' : 'Send Request'}
          onClick={requestState.sent ? hide : onSubmitRequest}
        />
      )}
    >
      <ModalContent>
        {requestState.sent ? (
          <SentRequestMessage
            email={contactEmail}
            failed={requestState.failed}
          />
        ) : (
          <>
            <Text variant="body2">
              Enter your email to request access to team commenting on this media. We&apos;ll send
              you an email with a link to confirm your request.
            </Text>
            <Text
              variant="caption"
              bold={true}
            >
              Email
            </Text>
            <Input
              ref={inputRef}
              placeholder="lenny@wistia.com"
              fullWidth={true}
              hasError={hasInputError}
            />
          </>
        )}
      </ModalContent>
    </ActionModal>
  );
};

export const RequestToBeAUserModal = ({
  contact,
  toggle,
  mediaId,
}: {
  contact: ContactData | null | undefined;
  toggle: ModalToggle;
  mediaId: string;
}): JSX.Element => {
  return isNil(contact) ? (
    <AnonymousModal
      toggle={toggle}
      mediaId={mediaId}
    />
  ) : (
    <LoggedInModal
      contact={contact}
      toggle={toggle}
      mediaId={mediaId}
    />
  );
};
